<template>
  <div class="user-menu" ref="userMenuRef">
    <button class="user-button" @click="toggleMainDropdown" :class="{ active: showMainDropdown }">
      <div class="user-avatar">
        <img v-if="photoUrl" class="user-icon" :src="photoUrl" alt="User avatar" @error="handleImageError" />
        <svg v-else class="user-icon-svg" width="36" height="36" viewBox="0 0 24 24" fill="none"
          v-html="getIconPath('user')"></svg>
      </div>
    </button>

    <Transition name="user-dropdown">
      <div v-if="showMainDropdown" class="dropdown">
        <div class="dropdown-content">
          <div class="user-info">
            <div class="user-avatar-large">
              <img v-if="photoUrl" class="user-icon-large" :src="photoUrl" alt="User avatar"
                @error="handleImageError" />
              <svg v-else class="user-icon-large-svg" width="48" height="48" viewBox="0 0 24 24" fill="none"
                v-html="getIconPath('user')"></svg>
            </div>
            <div class="user-details">
              <span class="user-name">{{ userProfile.displayName || userProfile.givenName || 'User' }}</span>
              <span class="user-email">{{ userProfile.mail || userProfile.userPrincipalName || 'user@example.com'
                }}</span>
            </div>
          </div>

          <div class="dropdown-divider"></div>

          <button class="menu-item logout-button" @click="handleLogout">
            <div class="menu-item-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" v-html="getIconPath('logout')"></svg>
            </div>
            <span class="menu-item-text">Log out</span>
          </button>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { msalService } from '../config/useAuth'
import { getUserPhoto, getUserProfile } from '@/Services/Ressources/AccountService'
import { getIconPath } from '@/Modules/utils/icons'

const userMenuRef = ref(null)
const showMainDropdown = ref(false)
const photoUrl = ref(null)
const userProfile = ref({})
let blobUrl = ''

const toggleMainDropdown = () => {
  showMainDropdown.value = !showMainDropdown.value
}

const handleLogout = async () => {
  showMainDropdown.value = false
  const { logout } = msalService()
  logout()
}

const handleClickOutside = (event) => {
  if (showMainDropdown.value && userMenuRef.value && !userMenuRef.value.contains(event.target)) {
    showMainDropdown.value = false
  }
}

const handleImageError = () => {
  photoUrl.value = null
  if (blobUrl) {
    URL.revokeObjectURL(blobUrl)
    blobUrl = ''
  }
}

onMounted(async () => {
  try {
    userProfile.value = await getUserProfile()

    try {
      blobUrl = await getUserPhoto()
      photoUrl.value = blobUrl
    } catch (photoError) {
      console.warn('Could not load user photo:', photoError)
    }
  } catch (error) {
    console.error('Error fetching user profile:', error)
  }
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  if (blobUrl) {
    URL.revokeObjectURL(blobUrl)
  }
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
.user-menu {
  position: relative;
  display: flex;
  align-items: center;
}

.user-button {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  border-radius: 50%;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
}

.user-button::before {
  content: '';
  position: absolute;
  top: -4px;
  left: -4px;
  right: -4px;
  bottom: -4px;
  background: var(--highlights-background);
  border-radius: 50%;
  opacity: 0;
  transition: opacity 0.2s ease;
  z-index: -1;
}

.user-button:hover::before {
  opacity: 1;
}

.user-button.active::before {
  opacity: 1;
  background: var(--highlights-background);
}

.user-avatar {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.user-icon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
}

.user-icon-svg {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
  background: var(--highlights-background, #f3f4f6);
  color: var(--highlights, #6b7280);
  padding: 6px;
}

.user-button:hover .user-icon,
.user-button:hover .user-icon-svg {
  transform: scale(1.05);
  border-color: var(--highlights);
  box-shadow: 0 4px 12px rgba(123, 24, 28, 0.2);
}

.dropdown {
  position: absolute;
  top: calc(100% + 12px);
  right: 0;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  box-shadow: 0 20px 25px rgba(0, 0, 0, 0.1), 0 10px 10px rgba(0, 0, 0, 0.04);
  z-index: 1000;
  min-width: 230px;
  overflow: hidden;
}

.dropdown-content {
  padding: 1.5rem;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
}

.user-avatar-large {
  flex-shrink: 0;
}

.user-icon-large {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid var(--highlights);
  box-shadow: 0 4px 8px rgba(123, 24, 28, 0.15);
}

.user-icon-large-svg {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 2px solid var(--highlights);
  box-shadow: 0 4px 8px rgba(123, 24, 28, 0.15);
  background: var(--highlights-background, #f3f4f6);
  color: var(--highlights, #6b7280);
  padding: 8px;
}

.user-details {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
  flex: 1;
}

.user-name {
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  line-height: 1.3;
  word-break: break-word;
}

.user-email {
  font-size: 13px;
  color: #6b7280;
  font-weight: 500;
  word-break: break-word;
}

.dropdown-divider {
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(0, 0, 0, 0.1), transparent);
  margin: 1rem 0;
}

.menu-item {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0.75rem 1rem;
  background: none;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
  gap: 0.75rem;
  position: relative;
  overflow: hidden;
}

.menu-item::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--highlights-background);
  opacity: 0;
  transition: opacity 0.2s ease;
}

.menu-item:hover::before {
  opacity: 1;
}

.menu-item:hover {
  transform: translateX(2px);
}

.menu-item-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: rgba(239, 68, 68, 0.1);
  border-radius: 8px;
  color: var(--highlights);
  flex-shrink: 0;
  transition: all 0.2s ease;
  position: relative;
  z-index: 1;
}

.menu-item:hover .menu-item-icon {
  background: var(--highlights);
  color: white;
  box-shadow: 0 2px 4px rgba(123, 24, 28, 0.25);
}

.menu-item-text {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  position: relative;
  z-index: 1;
  transition: color 0.2s ease;
}

.menu-item:hover .menu-item-text {
  color: #ef4444;
}

/* Animations */
.user-dropdown-enter-active,
.user-dropdown-leave-active {
  transition: all 0.2s ease;
}

.user-dropdown-enter-from {
  opacity: 0;
  transform: translateY(-8px) scale(0.98);
}

.user-dropdown-leave-to {
  opacity: 0;
  transform: translateY(-8px) scale(0.98);
}
</style>